import { useEffect, useState } from 'react';
import { Alert, Button, Flex, Modal, Typography } from 'antd';
import { SwapOutlined, SyncOutlined } from '@ant-design/icons';
import { TeamsIcon, ZoomlogiNewColorLogo } from '@/assets/icons/icons';
import { ConnectTeamWebhook } from '@/apis/settings';
import CustomInput from '@/components/CustomInput';
import CustomButton from '@/components/CustomButton';
import './TeamCommunication.scss';

interface Props {
  teamId?: string;
  token?: string;
  initialWebhook: string;
  onConnected: (url: string) => void;
}

export default function TeamsConnectionPanel({
  teamId,
  token,
  initialWebhook,
  onConnected,
}: Props) {
  const [webhook, setWebhook] = useState(initialWebhook);
  const [draft, setDraft] = useState(initialWebhook);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [removalPending, setRemovalPending] = useState(false);

  useEffect(() => {
    setWebhook(initialWebhook);
    setDraft(initialWebhook);
    setOpen(false);
    setError('');
    setRemovalPending(false);
  }, [initialWebhook, teamId]);

  const connect = async () => {
    if (!token || busy) return;
    const url = draft.trim();
    try {
      if (new URL(url).protocol !== 'https:') throw new Error();
    } catch {
      setError('Enter a valid HTTPS webhook URL from Teams Workflows.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await ConnectTeamWebhook(token, {
        team_id: teamId,
        provider: 'teams',
        webhook_url: url,
      });
      if (!response.data.data.is_success) throw new Error();
      setWebhook(url);
      setRemovalPending(false);
      onConnected(url);
      setOpen(false);
    } catch {
      setError(
        'We couldn’t post to this URL. Check that you copied the full webhook URL from Workflows and try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Flex align='center' gap={12} className='team-communication__card'>
        <span className='team-communication__icon'>
          <TeamsIcon />
        </span>
        <Flex vertical gap={2} className='team-communication__content'>
          <Flex align='center' gap={6}>
            <Typography.Text strong>Microsoft Teams</Typography.Text>
            {webhook && (
              <span className='team-communication__synced'>
                <SyncOutlined /> Synced
              </span>
            )}
          </Flex>
          <Typography.Text className='team-communication__hint'>
            {webhook
              ? 'Webhook connected'
              : 'Post notifications to a Teams channel'}
          </Typography.Text>
        </Flex>
        <Button
          disabled={!token || busy}
          onClick={() => {
            setDraft(webhook);
            setError('');
            setOpen(true);
          }}
        >
          {webhook ? 'Manage' : 'Connect'}
        </Button>
      </Flex>
      {removalPending && (
        <Typography.Text className='team-communication__hint' role='status'>
          Save the team to stop Teams notifications.
        </Typography.Text>
      )}
      <Modal
        open={open}
        centered
        width={519}
        footer={null}
        closable={!busy}
        title='Zoomlogi notifications wants to post in Teams'
        className='communication-setup-modal fs-exclude rr-block rr-ignore'
        maskClosable={!busy}
        keyboard={!busy}
        onCancel={() => {
          if (!busy) setOpen(false);
        }}
      >
        <Flex
          justify='center'
          align='center'
          gap={16}
          className='communication-setup-modal__logos'
        >
          <span className='team-communication__icon'>
            <TeamsIcon />
          </span>
          <SwapOutlined />
          <span className='team-communication__icon'>
            <ZoomlogiNewColorLogo width={20} height={18} />
          </span>
        </Flex>
        <Flex vertical gap={20}>
          <Typography.Text className='team-communication__hint'>
            Create a webhook in Teams, then paste it here. Notifications will
            post to the channel you pick.
          </Typography.Text>
          <ol className='team-communication__instructions'>
            <li>
              In Teams, open <strong>Workflows</strong> from Apps.
            </li>
            <li>
              Choose the template{' '}
              <strong>
                Post to a channel when a webhook request is received
              </strong>
              , then pick the team and channel.
            </li>
            <li>Copy the webhook URL Teams gives you and paste it below.</li>
          </ol>
          <Flex vertical gap={6}>
            <label htmlFor='teams-webhook-url'>Webhook URL</label>
            <CustomInput
              id='teams-webhook-url'
              value={draft}
              disabled={busy}
              placeholder='https://...logic.azure.com/workflows/...'
              className='statsig-no-capture'
              classNames={{ input: 'statsig-no-capture' }}
              autoComplete='off'
              onChange={(event) => {
                setDraft(event.target.value);
                setError('');
              }}
            />
            <Typography.Text className='team-communication__hint'>
              We send a test message to the channel to confirm it works.
            </Typography.Text>
            {!teamId && (
              <Typography.Text className='team-communication__hint'>
                Save the team after connecting to keep this webhook.
              </Typography.Text>
            )}
          </Flex>
          {error && <Alert type='error' message={error} />}
          <Flex justify='flex-end' gap={12}>
            {webhook && (
              <Button
                danger
                type='text'
                disabled={!token || busy}
                onClick={() => {
                  setWebhook('');
                  setRemovalPending(true);
                  setDraft('');
                  setError('');
                  onConnected('');
                  setOpen(false);
                }}
              >
                Remove connection
              </Button>
            )}
            <Button disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <CustomButton
              text={busy ? 'Connecting…' : 'Connect'}
              disabled={busy || !draft.trim() || !token}
              bgColor='var(--text-primary)'
              color='var(--surface-container)'
              height={36}
              borderRadius='8px'
              onClick={() => {
                connect().catch((): void => {});
              }}
            />
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}
